import {
  IUseQuantityControlProps,
  IUseQuantityControlReturn,
} from "@model/UIComponents.model";
import { useEffect, useState } from "react";
import { clampItemQuantity } from "../../../Utils/Global/globalfuncion";

const EPSILON = 0.000001;

export const useQuantityControl = ({
  value,
  onChange,
  onPreviewChange,
  step = 1,
  min = 1,
  max,
  disabled = false,
  onError,
}: IUseQuantityControlProps): IUseQuantityControlReturn => {
  const [inputValue, setInputValue] = useState<string>(value.toString());
  const [error, setError] = useState<string>("");

  const decimalPlaces = (step.toString().split(".")[1] || "").length;
  const maximumAllowedBound =
    typeof max === "number" ? max : Number.POSITIVE_INFINITY;

  useEffect(() => {
    setInputValue(value.toString());
  }, [value]);

  useEffect(() => {
    onError?.(error || null);
  }, [error, onError]);

  const commitClampedQuantity = (next: number) => {
    const clamped = clampItemQuantity(next, min, maximumAllowedBound);
    onChange(Number(clamped.toFixed(decimalPlaces)));
  };

  const validateAndCommit = (candidate: number) => {
    if (disabled) return;

    if (!Number.isFinite(candidate)) {
      setError("Please enter a valid number");
      return;
    }
    if (step % 1 === 0 && !Number.isInteger(candidate)) {
      setError("Only integers are allowed");
      return;
    }

    if (candidate < min - EPSILON) {
      setError(`Minimum allowed is ${min}`);
      commitClampedQuantity(min);
      return;
    }
    if (candidate > maximumAllowedBound + EPSILON) {
      setError(
        Number.isFinite(maximumAllowedBound)
          ? `Maximum allowed quantity is ${maximumAllowedBound}`
          : "Exceeded maximum"
      );
      commitClampedQuantity(maximumAllowedBound);
      return;
    }

    setError("");
    commitClampedQuantity(candidate);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled) return;
    const raw = e.target.value;

    if (raw === "") {
      setInputValue("");
      setError("Please enter a valid number");
      return;
    }

    const parsed = Number(raw);
    if (!Number.isFinite(parsed)) {
      setError("Please enter a valid number");
      return;
    }

    if (parsed < min) {
      setInputValue(String(min));
      setError(`Minimum allowed is ${min}`);
      onPreviewChange?.(min);
      return;
    }
    if (parsed > maximumAllowedBound) {
      setInputValue(String(maximumAllowedBound));
      setError(
        Number.isFinite(maximumAllowedBound)
          ? `Maximum allowed quantity is ${maximumAllowedBound}`
          : "Exceeded maximum"
      );
      onPreviewChange?.(maximumAllowedBound);
      return;
    }

    setInputValue(raw);
    setError("");
    onPreviewChange?.(parsed);
  };

  const handleInputBlur = () => {
    if (disabled) return;

    if (inputValue.trim() === "") {
      setError("Please enter a valid number");
      return;
    }

    const parsed = Number(inputValue);
    if (!Number.isFinite(parsed)) {
      setError("Please enter a valid number");
      return;
    }
    validateAndCommit(parsed);
  };

  // User entered value
  const currentNumeric = Number(inputValue);
  // Sanitized number (in case user enters an invalid
  // character or empty value it falls back to previous value)
  const currentEffective = Number.isFinite(currentNumeric)
    ? currentNumeric
    : value;

  const disableDecrement = disabled || currentEffective <= min + EPSILON;
  const disableIncrement =
    disabled ||
    (Number.isFinite(maximumAllowedBound) &&
      currentEffective >= maximumAllowedBound - EPSILON);

  const handleIncrease = () => {
    if (!disableIncrement) validateAndCommit(currentEffective + step);
  };

  const handleDecrease = () => {
    if (!disableDecrement) validateAndCommit(currentEffective - step);
  };

  return {
    inputValue,
    error,
    handleInputChange,
    handleInputBlur,
    handleIncrease,
    handleDecrease,
    disableIncrement,
    disableDecrement,
  };
};
